import React from 'react';
import SelectDropdown from '../../components/select';
import { GeneralContractorFormData, FormErrors, Province } from './types';

interface GeneralContractorFormProps {
  formData: GeneralContractorFormData;
  errors: FormErrors;
  currentStep: number;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  provinceOptions: Province[];
  isLoadingProvinces: boolean;
  setFormData: React.Dispatch<React.SetStateAction<GeneralContractorFormData>>;
  isEditMode?: boolean;
  canDeactivate?: boolean;
  deactivationReason?: string;
  hideStatusToggle?: boolean;
}

const GeneralContractorForm: React.FC<GeneralContractorFormProps> = ({
  formData,
  errors,
  currentStep,
  handleInputChange,
  provinceOptions,
  isLoadingProvinces,
  setFormData,
  isEditMode = false,
  canDeactivate = true,
  deactivationReason = '',
  hideStatusToggle = false
}) => {
  if (currentStep === 1) {
    // Step 1: Basic Information (Name)
    return (
      <>
        <div className="pb-4">
          <label className="block text-small font-medium text-text-secondary mb-1">
            General Contractor Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="name"
            id="input-name"
            autoFocus
            placeholder="Enter general contractor name"
            className={`w-full px-3 py-2 text-body border ${errors.name ? 'border-red-500' : 'border-border'} rounded-input focus:outline-none focus:ring-1 focus:ring-black focus:border-black focus:ring-opacity-0`}
            value={formData.name}
            onChange={handleInputChange}
          />
          {errors.name && (
            <p className="mt-1 text-xs text-red-500">{errors.name}</p>
          )}
        </div>

        {!hideStatusToggle && (
          <div className="pb-4">
            <label className="block text-small font-medium text-text-secondary mb-1">
              Status
            </label>
            <div
              className="flex items-center mt-2"
              title={isEditMode && formData.active && !canDeactivate && deactivationReason ? `Cannot inactivate: ${deactivationReason}` : ''}
            >
              <div className="relative inline-flex items-center">
                <label className="relative flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.active === true}
                    onChange={(e) => {
                      if (!e.target.checked && isEditMode && !canDeactivate) {
                        e.preventDefault();
                        return;
                      }
                      setFormData(prev => ({ ...prev, active: e.target.checked }));
                    }}
                    disabled={isEditMode && formData.active && !canDeactivate}
                    className="sr-only peer"
                    id="general-contractor-status-toggle"
                  />
                  <div className={`
                    w-11 h-6 rounded-full
                    ${isEditMode && formData.active && !canDeactivate
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-gray-200 peer-checked:bg-primary cursor-pointer'}
                    after:content-[''] after:absolute after:top-[2px] after:left-[2px]
                    after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5
                    after:transition-all peer-checked:after:translate-x-full peer-checked:after:border-white
                  `}></div>
                </label>
              </div>
              <span className="ml-2 text-sm">
                {formData.active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        )}
      </>
    );
  }

  // Step 2: Address Information (Optional)
  return (
    <>
      <div className="mb-6">
        <h4 className="text-lg font-medium text-gray-900 mb-2">Address Information</h4>
        <p className="text-sm text-gray-600 mb-4">
          Address information is optional but can help identify the general contractor.
        </p>
      </div>

      <div className="pb-4">
        <label className="block text-small font-medium text-text-secondary mb-1">
          Street Address
        </label>
        <input
          type="text"
          name="address"
          id="input-address"
          placeholder="Enter street address"
          className={`w-full px-3 py-2 text-body border ${errors.address ? 'border-red-500' : 'border-border'} rounded-input focus:outline-none focus:ring-1 focus:ring-black focus:border-black focus:ring-opacity-0`}
          value={formData.address}
          onChange={handleInputChange}
        />
        {errors.address && (
          <p className="mt-1 text-xs text-red-500">{errors.address}</p>
        )}
      </div>

      <div className="pb-4">
        <label className="block text-small font-medium text-text-secondary mb-1">
          City
        </label>
        <input
          type="text"
          name="city"
          id="input-city"
          placeholder="Enter city"
          className={`w-full px-3 py-2 text-body border ${errors.city ? 'border-red-500' : 'border-border'} rounded-input focus:outline-none focus:ring-1 focus:ring-black focus:border-black focus:ring-opacity-0`}
          value={formData.city}
          onChange={handleInputChange}
        />
        {errors.city && (
          <p className="mt-1 text-xs text-red-500">{errors.city}</p>
        )}
      </div>

      <div className="pb-4">
        <label className="block text-small font-medium text-text-secondary mb-1">
          Province
        </label>
        {isLoadingProvinces ? (
          <div className="w-full px-3 py-2 border border-border rounded-input text-gray-500">
            Loading provinces...
          </div>
        ) : (
          <SelectDropdown
            testId="province-select"
            options={provinceOptions}
            getValue={(option) => option.id}
            getDisplayText={(option) => option.name}
            onSelect={(value) => setFormData(prev => ({ ...prev, province: value }))}
            selectedValue={formData.province}
            placeholder="Select province"
          />
        )}
        {errors.province && (
          <p className="mt-1 text-xs text-red-500">{errors.province}</p>
        )}
      </div>

      <div className="pb-4">
        <label className="block text-small font-medium text-text-secondary mb-1">
          Postal Code
        </label>
        <input
          type="text"
          name="postalCode"
          id="input-postal-code"
          placeholder="Enter postal code (e.g., K1A 0A6)"
          className={`w-full px-3 py-2 text-body border ${errors.postalCode ? 'border-red-500' : 'border-border'} rounded-input focus:outline-none focus:ring-1 focus:ring-black focus:border-black focus:ring-opacity-0`}
          value={formData.postalCode}
          onChange={handleInputChange}
        />
        {errors.postalCode && (
          <p className="mt-1 text-xs text-red-500">{errors.postalCode}</p>
        )}
      </div>

      {/* Summary Section */}
      <div className="mt-8 p-4 bg-gray-50 rounded-lg">
        <h5 className="font-medium text-gray-900 mb-3">Summary</h5>
        <div className="space-y-2 text-sm">
          <div>
            <span className="font-medium">Name:</span> {formData.name || 'Not provided'}
          </div>
          {!hideStatusToggle && (
            <div>
              <span className="font-medium">Status:</span> {formData.active ? 'Active' : 'Inactive'}
            </div>
          )}
          {formData.address && (
            <div>
              <span className="font-medium">Address:</span> {formData.address}
            </div>
          )}
          {formData.city && formData.province && (
            <div>
              <span className="font-medium">Location:</span> {formData.city}, {
                provinceOptions.find(p => p.id === formData.province)?.name || formData.province
              }
            </div>
          )}
          {formData.postalCode && (
            <div>
              <span className="font-medium">Postal Code:</span> {formData.postalCode}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default GeneralContractorForm;